import { Button, Flex, Text } from "@mantine/core";
import {
  Modal,
  PasswordInputControlled,
  PasswordRequirements,
} from "../../../components";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationRecovery } from "../../../utils/validation/schemas/validationChangePassword";
import { useState } from "react";
import { IFormChangePassword } from "../../../interfaces/User/newUser";
import { useNotificationControl } from "../../../context/notificationContext";
import { useMutation } from "@tanstack/react-query";
import { postChangePassword } from "../../../hooks/useUsers";
import { useAuth } from "../../../context/auth";
import { useTranslation } from "react-i18next";

const Password: React.FC = () => {
  const { logout } = useAuth();
  const { notificationControl } = useNotificationControl();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const methods = useForm<IFormChangePassword>({
    mode: "onChange",
    resolver: yupResolver(ValidationRecovery),
    defaultValues: {
      old_password: "",
      new_password: "",
      second_new_password: "",
    },
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = methods;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const { mutate: changePassword } = useMutation(postChangePassword, {
    onSuccess: () => {
      setOpenModal(!openModal);
      setIsLoading(false);
    },
    onError: (errors: any) => {
      console.error("Erro ao editar usuario", errors);
      notificationControl({
        message: t("meu_perfil.senha.error_message"),
        type: "danger",
      });
      setIsLoading(false);
    },
  });

  const onSubmit = (data: IFormChangePassword) => {
    setIsLoading(true);
    changePassword(data);
  };

  return (
    <>
      <Modal
        type="success"
        titleSeccondButton={t("global.modal.close")}
        description={t("meu_perfil.senha.modal_success_description")}
        open={openModal}
        setClose={logout}
        secondClick={logout}
      />
      <Flex direction="column" justify="center" align="start" w="100%" gap={7}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Flex
              id="editDataProfile"
              direction="column"
              bg={"basic.0"}
              p={{ base: 16, 991: 24, 1400: 48 }}
              gap={{ base: 24 }}
              w="100%"
              sx={{
                borderRadius: "12px",
                boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
              }}
            >
              <Text
                fw={{ base: 600 }}
                fz={{ base: 24 }}
                lh={{ base: "37.2px" }}
              >
                {t("meu_perfil.senha.title")}{" "}
              </Text>
              <PasswordInputControlled
                name="old_password"
                label={t("global.input.old_password.label")}
                placeholder={t("global.input.old_password.placeholder")}
                withAsterisk
              />

              <Flex
                gap={{ base: 24 }}
                direction={{ base: "column", 1504: "row" }}
              >
                <PasswordInputControlled
                  name="new_password"
                  label={t("global.input.new_password.label")}
                  placeholder={t("global.input.new_password.placeholder")}
                  withAsterisk
                  w="100%"
                />

                <PasswordInputControlled
                  name="second_new_password"
                  label={t("global.input.second_new_password.label")}
                  placeholder={t(
                    "global.input.second_new_password.placeholder"
                  )}
                  withAsterisk
                  w="100%"
                />
              </Flex>
              <PasswordRequirements
                passwordOne={watch("new_password")}
                passwordTwo={watch("second_new_password")}
              />
              <Button
                variant="primary"
                type="submit"
                disabled={!isDirty || !isValid}
                loading={isLoading}
              >
                {t("meu_perfil.senha.title")}
              </Button>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </>
  );
};

export default Password;
